import React from "react";
import {
  ExternalLink,
  Award,
  GraduationCap,
  Network,
  Users,
  BriefcaseBusiness,
  Laptop,
  Gamepad2,
  Building2,
  Sparkles,
} from "lucide-react";

import { projetos } from "../data/mockData";

const ProjectsSection = () => {
  const categoryIcons = {
    "Educação e Tecnologia": GraduationCap,
    "Inovação e Empreendedorismo": Sparkles,
    "Terceiro Setor": Users,
    "Empreendedorismo": BriefcaseBusiness,
    "Desenvolvimento de Negócios": Building2,
    "Tecnologia e Educação": Laptop,
    "Tecnologia e Economia Criativa": Gamepad2,
    "Ecossistemas de Inovação": Network,
  };

  const statusColors = {
    "Em execução": "bg-green-100 text-green-800",
    "Concluído": "bg-gray-100 text-gray-700",
    "Contínuo": "bg-blue-100 text-blue-800",
    "Realizado": "bg-emerald-100 text-emerald-800",
    "Em desenvolvimento": "bg-amber-100 text-amber-800",
  };

  const scrollToSection = (sectionId) => {
    const element = document.getElementById(sectionId);

    if (element) {
      const headerOffset = 90;

      const elementPosition =
        element.getBoundingClientRect().top + window.pageYOffset;

      const offsetPosition = elementPosition - headerOffset;

      window.scrollTo({
        top: offsetPosition,
        behavior: "smooth",
      });
    }
  };

  return (
    <section id="projects" className="py-20 lg:py-28 bg-gray-50">
      <div className="container">
        {/* Section Header */}
        <div className="text-center mb-14 lg:mb-16">
          <span
            className="
              inline-flex
              items-center
              gap-2
              px-4
              py-2
              mb-5
              rounded-full
              bg-green-100
              text-green-700
              text-sm
              font-semibold
            "
          >
            <Sparkles className="w-4 h-4" />
            Inovação que gera desenvolvimento
          </span>

          <h2 className="heading-2 mb-6">
            Projetos e Iniciativas
          </h2>

          <p className="body-large max-w-3xl mx-auto text-gray-700 leading-relaxed">
            Conheça algumas das iniciativas desenvolvidas e executadas pelo IMA
            para fortalecer a inovação e o desenvolvimento da Amazônia.
          </p>
        </div>

        {/* Projects Grid */}
        <div className="grid md:grid-cols-2 gap-8">
          {projetos.map((projeto, index) => {
            const IconComponent =
              categoryIcons[projeto.category] || Sparkles;

            const statusClass =
              statusColors[projeto.status] ||
              "bg-gray-100 text-gray-700";

            return (
              <div
                key={`${projeto.title}-${index}`}
                className="
                  innovation-card
                  group
                  bg-white
                  border
                  border-gray-100
                  rounded-2xl
                  p-6
                  transition-all
                  duration-300
                  hover:-translate-y-1
                  hover:shadow-lg
                  hover:border-green-200
                "
              >
                {/* Header */}
                <div className="flex items-start justify-between gap-4 mb-5">
                  <div className="flex items-start gap-3">
                    <div
                      className="
                        w-11
                        h-11
                        shrink-0
                        bg-accent-wash
                        rounded-xl
                        flex
                        items-center
                        justify-center
                        group-hover:bg-green-100
                        transition-colors
                      "
                    >
                      <IconComponent className="w-5 h-5 text-accent-text" />
                    </div>

                    <div>
                      <h3 className="heading-3 text-lg leading-snug">
                        {projeto.title}
                      </h3>

                      <div className="flex flex-wrap items-center gap-2 mt-2">
                        {projeto.status && (
                          <span
                            className={`
                              px-2.5
                              py-1
                              rounded-full
                              text-xs
                              font-medium
                              ${statusClass}
                            `}
                          >
                            {projeto.status}
                          </span>
                        )}

                        {projeto.category && (
                          <span className="text-xs text-gray-500">
                            {projeto.category}
                          </span>
                        )}
                      </div>
                    </div>
                  </div>

                  {projeto.link && (
                    <a
                      href={projeto.link}
                      target="_blank"
                      rel="noopener noreferrer"
                      aria-label={`Saiba mais sobre ${projeto.title}`}
                      className="
                        shrink-0
                        text-gray-400
                        hover:text-accent-text
                        transition-colors
                      "
                    >
                      <ExternalLink className="w-5 h-5" />
                    </a>
                  )}
                </div>

                {/* Área de atuação */}
                {projeto.area && (
                  <div className="mb-4">
                    <span className="text-xs uppercase tracking-wide text-gray-400 font-semibold">
                      Área de atuação
                    </span>

                    <p className="text-sm font-medium text-gray-700 mt-1">
                      {projeto.area}
                    </p>
                  </div>
                )}

                {/* Description */}
                <p className="body-medium text-gray-600 leading-relaxed mb-5">
                  {projeto.description}
                </p>

                {/* Papel do IMA */}
                {projeto.role && (
                  <div
                    className="
                      bg-gray-50
                      border border-gray-100
                      rounded-xl
                      p-4
                      mb-4
                    "
                  >
                    <span className="text-xs uppercase tracking-wide text-gray-400 font-semibold">
                      Papel do IMA
                    </span>

                    <p className="text-sm font-medium text-gray-800 mt-1">
                      {projeto.role}
                    </p>
                  </div>
                )}

                {/* Impacto - somente se houver dado comprovado */}
                {projeto.impact && (
                  <div className="bg-accent-wash/50 p-4 rounded-xl">
                    <div className="flex items-center gap-2 mb-2">
                      <Award className="w-4 h-4 text-accent-text" />

                      <span className="text-sm font-medium text-accent-text">
                        Resultado ou impacto
                      </span>
                    </div>

                    <p className="text-sm font-semibold text-gray-800">
                      {projeto.impact}
                    </p>
                  </div>
                )}                
              </div>
            );
          })}
        </div>

        {/* CTA */}
        <div className="mt-16">
          <div
            className="
              bg-white
              p-8
              md:p-10
              rounded-2xl
              shadow-sm
              border
              border-green-100
              text-center
            "
          >
            <Network className="w-11 h-11 text-accent-text mx-auto mb-4" />

            <h3 className="heading-3 mb-4">
              Vamos transformar a Amazônia juntos?
            </h3>

            <p className="body-medium text-gray-600 mb-6 max-w-2xl mx-auto leading-relaxed">
              Conectamos pessoas, organizações e oportunidades para desenvolver
              soluções que geram inovação, desenvolvimento e impacto na Amazônia.
            </p>

            <button
              type="button"
              onClick={() => scrollToSection("contact")}
              className="btn-primary"
            >
              Fale com o IMA
            </button>
          </div>
        </div>
      </div>
    </section>
  );
};

export default ProjectsSection;